import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Button, Grid, InputLabel, OutlinedInput, Switch, FormControl, Select, MenuItem, Typography } from '@mui/material';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import { validateNumber } from 'utils/commonHelpers';
import { gridSpacing } from 'utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { getSettings, putSettings, updateSettings } from 'components/../redux/actions/settings';
import ApiHelper from '../../utils/apiHelper';

const GeneralSettings = () => {
    const { userInfo } = useSelector((state) => state.auth);
    const settings = useSelector((state) => state.settings);
    const dispatch = useDispatch();
    const [dashSelOptions, setDashSelOptions] = useState([]);
    const [dashboardSelectionOrderValues, setDashboardSelectionOrderValues] = useState([]);
    const [equivalentOptions, setEquivalentOptions] = useState([]);
    const [avoidanceOptions, setAvoidanceOptions] = useState([]);
    const [sequesteredOptions, setSequesteredOptions] = useState([]);
    const Currencies = [{ USD: '$' }, { EUR: '€' }, { GBP: '£' }, { JPY: '¥' }, { INR: '₹' }, { KRW: '₩' }];

    useEffect(() => {
        getDashboardSelectionOptions();
        getEquivalentOptions();
        getAvoidanceOptions();
        getSequesteredOptions();
        dispatch(getSettings());
    }, [dispatch]);

    const getEquivalentOptions = () => {
        ApiHelper.get('/api/settings/constants/', {}, {}, { values: 'equivalent_options' }).then((res) => {
            setEquivalentOptions(res.data['equivalentOptions']);
        });
    };
    const getAvoidanceOptions = () => {
        ApiHelper.get('/api/settings/constants/', {}, {}, { values: 'avoidance_options' }).then((res) => {
            setAvoidanceOptions(res.data['avoidanceOptions']);
        });
    };
    const getSequesteredOptions = () => {
        ApiHelper.get('/api/settings/constants/', {}, {}, { values: 'sequestered_options' }).then((res) => {
            setSequesteredOptions(res.data['sequesteredOptions']);
        });
    };

    const getDashboardSelectionOptions = () => {
        ApiHelper.get('/api/settings/dash_sel_options/')
            .then((res) => {
                if (res.status === 200) {
                    const list = [];
                    setDashSelOptions(res.data);
                    for (let i = 1; i <= res.data.length; i++) {
                        list.push(i);
                    }
                    setDashboardSelectionOrderValues(list);
                } else if (res.status === 404) {
                    toast.error(res.data.message);
                }
            })
            .catch((err) => {});
    };

    const handleChangeSettingsData = (e) => {
        if (e.target.value === '') toast.error('Please type the value');
        const type = e.target.id === 'generalSettings' ? 'integer' : 'number';
        if (validateNumber(e.target.value, type) === true) {
            dispatch(
                updateSettings({
                    ...settings,
                    [e.target.id]: {
                        ...settings[e.target.id],
                        [e.target.name]: e.target.value
                    }
                })
            );
        } else {
            if (type === 'integer') toast.error('It should be an integer value.');
            else toast.error('It should be a number');
        }
    };

    const handleCancelSubmit = (e) => {
        dispatch(getSettings());
    };

    const handleChangeGreenhouseData = (e) => {
        dispatch(
            updateSettings({
                ...settings,
                generalSettings: {
                    ...settings['generalSettings'],
                    [e.target.name]: e.target.value
                }
            })
        );
    };

    const handleChangeDashboardPanelOrder = (e) => {
        let newData = { ...settings };
        for (const key in newData.selectedDashboards) {
            if (newData.selectedDashboards.hasOwnProperty(key)) {
                if (Number(e.target.value) !== 0 && newData.selectedDashboards[key].orderVal === Number(e.target.value)) {
                    newData.selectedDashboards[key].orderVal = 0;
                }
            }
        }
        newData.selectedDashboards[e.target.name]['orderVal'] = Number(e.target.value);
        dispatch(updateSettings(newData));
    };

    const handleChangeCurrency = (e) => {
        dispatch(
            updateSettings({
                ...settings,
                unitsValues: {
                    ...settings.energyValues,
                    currency: e.target.value
                }
            })
        );
    };

    const handleChangeUnits = (e) => {
        dispatch(
            updateSettings({
                ...settings,
                unitsValues: {
                    ...settings.energyValues,
                    units: e.target.value
                }
            })
        );
    };

    const handleSubmit = () => {
        if (
            settings.generalSettings.showRows === '' ||
            settings.generalSettings.checkinInterval === '' ||
            settings.generalSettings.showUnlicensedFor === '' ||
            settings.generalSettings.unlicensedAfter === '' ||
            settings.generalSettings.dataRetention === '' ||
            settings.energyValues.energyCost === '' ||
            settings.energyValues.co2Cost === '' ||
            settings.energyValues.greenPowerPercent === '' ||
            settings.energyValues.currency === '' ||
            settings.energyValues.units === ''
        ) {
            toast.error('There are some empty values');
            return true;
        }
        dispatch(putSettings(settings));
    };
    if (!settings?.generalSettings || Object.keys(settings?.generalSettings).length === 0) {
        return null;
    } else {
        return (
            <MainCard title="General Settings" cardHeaderTitle="System Options" cardHeaderShow={true}>
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12} sx={{ position: 'relative', mt: 2, mb: 2 }}>
                        <Box sx={{ position: 'absolute', right: '0', top: '0' }}>
                            <Button size="large" color="secondary" variant="contained" onClick={handleSubmit}>
                                Save
                            </Button>
                            <Button onClick={handleCancelSubmit}>Cancel</Button>
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <SubCard title="General Settings">
                            <Grid container>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Data retention (Days) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={settings.generalSettings.dataRetention}
                                        name="dataRetention"
                                        id="generalSettings"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Check-in interval (Minutes) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={settings.generalSettings.checkinInterval}
                                        name="checkinInterval"
                                        id="generalSettings"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Unlicense After (Days) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={settings.generalSettings.unlicensedAfter}
                                        name="unlicensedAfter"
                                        id="generalSettings"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Show Unlicensed For (Days) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={settings.generalSettings.showUnlicensedFor}
                                        name="showUnlicensedFor"
                                        id="generalSettings"
                                        onChange={handleChangeSettingsData}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Show Unlicensed Devices </InputLabel>
                                    <Switch
                                        color="success"
                                        checked={settings.generalSettings.showUnlicensedDevices}
                                        name="showUnlicensedDevices"
                                        id="generalSettings"
                                        onChange={(e) =>
                                            dispatch(
                                                updateSettings({
                                                    ...settings,
                                                    [e.target.id]: {
                                                        ...settings[e.target.id],
                                                        [e.target.name]: e.target.checked
                                                    }
                                                })
                                            )
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                    <Grid item xs={3}>
                        <SubCard title="Energy Values">
                            <Grid container>
                                <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                    <InputLabel>Energy Cost ({settings.energyValues.currency}/kWh) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={settings.energyValues.energyCost}
                                        name="energyCost"
                                        id="energyValues"
                                        onChange={(e) => {
                                            if (validateNumber(e.target.value, 'decimal', 4) !== true) {
                                                toast.error('Please enter a valid decimal number with up to 4 decimal places.');
                                                return;
                                            } else {
                                                handleChangeSettingsData(e);
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>CO2 ({settings.energyValues.units}/kWh) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={settings.energyValues.co2Cost}
                                        name="co2Cost"
                                        id="energyValues"
                                        onChange={(e) => {
                                            if (validateNumber(e.target.value, 'decimal', 3) !== true) {
                                                toast.error('Please enter a valid decimal number with up to 3 decimal places.');
                                                return;
                                            } else {
                                                handleChangeSettingsData(e);
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Green Power (%) </InputLabel>
                                    <OutlinedInput
                                        sx={{ width: '100%' }}
                                        value={settings.energyValues.greenPowerPercent}
                                        name="greenPowerPercent"
                                        id="energyValues"
                                        onChange={(e) => {
                                            if (validateNumber(e.target.value, 'decimal', 3) !== true) {
                                                toast.error('Please enter a valid decimal number with up to 3 decimal places.');
                                                return;
                                            } else {
                                                handleChangeSettingsData(e);
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </SubCard>
                        <SubCard title="Units" sx={{ mt: 4 }}>
                            <Grid container>
                                <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                    <InputLabel>Currency </InputLabel>
                                    <FormControl sx={{ width: '100%' }}>
                                        <Select
                                            displayEmpty
                                            inputProps={{ 'aria-label': 'Without label' }}
                                            value={settings.energyValues.currency}
                                            name="currency"
                                            onChange={handleChangeCurrency}
                                        >
                                            {Currencies.map((currency, key) => {
                                                const [code, symbol] = Object.entries(currency)[0];
                                                return (
                                                    <MenuItem key={key} value={symbol}>
                                                        {code} ({symbol})
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Weight </InputLabel>
                                    <FormControl sx={{ width: '100%' }}>
                                        <Select
                                            displayEmpty
                                            inputProps={{ 'aria-label': 'Without label' }}
                                            value={settings.energyValues['units']}
                                            name="units"
                                            onChange={handleChangeUnits}
                                        >
                                            <MenuItem value="lbs">lbs</MenuItem>
                                            <MenuItem value="kgs">kgs</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </SubCard>
                    </Grid>
                    <Grid item xs={3}>
                        <SubCard title="Greenhouse Gas Comparisons">
                            <Grid container>
                                <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                    <InputLabel>Preferred Equivalent</InputLabel>
                                    <FormControl sx={{ width: '100%' }}>
                                        <Select
                                            displayEmpty
                                            inputProps={{ 'aria-label': 'Without label' }}
                                            value={settings.generalSettings.equivalentId}
                                            name="equivalentId"
                                            onChange={handleChangeGreenhouseData}
                                        >
                                            {equivalentOptions.map((equivalent, key) => (
                                                <MenuItem value={equivalent.value} key={key}>
                                                    {equivalent.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Preferred Avoidance</InputLabel>
                                    <FormControl sx={{ width: '100%' }}>
                                        <Select
                                            displayEmpty
                                            inputProps={{ 'aria-label': 'Without label' }}
                                            value={settings.generalSettings.avoidanceId}
                                            name="avoidanceId"
                                            onChange={handleChangeGreenhouseData}
                                        >
                                            {avoidanceOptions.map((avoidance, key) => (
                                                <MenuItem value={avoidance.value} key={key}>
                                                    {avoidance.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }}>
                                    <InputLabel>Preferred Sequestration</InputLabel>
                                    <FormControl sx={{ width: '100%' }}>
                                        <Select
                                            displayEmpty
                                            inputProps={{ 'aria-label': 'Without label' }}
                                            value={settings.generalSettings.sequesteredId}
                                            name="sequesteredId"
                                            onChange={handleChangeGreenhouseData}
                                        >
                                            {sequesteredOptions.map((sequestered, key) => (
                                                <MenuItem value={sequestered.value} key={key}>
                                                    {sequestered.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </SubCard>
                        {(userInfo.admin_permissions.server_admin || userInfo.admin_permissions.root_admin) && (
                            <SubCard title="Logging Level" sx={{ mt: 4 }}>
                                <Grid container>
                                    <Grid item xs={12} sx={{ padding: '0 5px' }}>
                                        <InputLabel>Level</InputLabel>
                                        <FormControl sx={{ width: '100%' }}>
                                            <Select
                                                displayEmpty
                                                inputProps={{ 'aria-label': 'Without label' }}
                                                id="levelSettings"
                                                value={settings.levelSettings.debugLevelId}
                                                name="debugLevelId"
                                                onChange={(e) => {
                                                    dispatch(
                                                        updateSettings({
                                                            ...settings,
                                                            levelSettings: {
                                                                ...settings['levelSettings'],
                                                                [e.target.name]: Number(e.target.value)
                                                            }
                                                        })
                                                    );
                                                }}
                                            >
                                                {settings?.levelOptions &&
                                                    settings.levelOptions?.map((level, key) => (
                                                        <MenuItem value={level.levelid} key={key}>
                                                            {level.name}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </SubCard>
                        )}
                    </Grid>
                    <Grid item xs={3}>
                        <SubCard title="Dashboard Panel Selections">
                            <Grid container>
                                <Typography>Please select the dashboard order to show the plots at Home page</Typography>
                                {dashSelOptions.map((data, key) => (
                                    <Grid item xs={12} sx={{ padding: '0 5px', mt: 2 }} key={key}>
                                        <InputLabel>{data.title}</InputLabel>
                                        <FormControl sx={{ width: '100%' }}>
                                            <Select
                                                displayEmpty
                                                inputProps={{ 'aria-label': 'Without label' }}
                                                name={data.name}
                                                value={
                                                    settings.selectedDashboards[data.name]
                                                        ? settings.selectedDashboards[data.name]['orderVal']
                                                        : 0
                                                }
                                                onChange={handleChangeDashboardPanelOrder}
                                            >
                                                <MenuItem value={0}>Don't show</MenuItem>
                                                {dashboardSelectionOrderValues.map((value, key) => (
                                                    <MenuItem value={value} key={key}>
                                                        {value}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                ))}
                            </Grid>
                        </SubCard>
                    </Grid>
                </Grid>
            </MainCard>
        );
    }
};

export default GeneralSettings;
